import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  Input,
  TemplateRef,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { I18nService } from '@core/i18n-legacy';

import type { IconName } from '../../../common/icon/icons.type';
import { BadgeComponent } from '../../badge/badge.component';
import type { BadgeColor } from '../../badge/badge.types';
import { CircularProgressIndicatorComponent } from '../../circular-progress-indicator/circular-progress-indicator.component';
import { DotComponent } from '../../dot/dot.component';
import type { IconToken } from '../../icon/directives/icon-color.directive';
import { IconComponent } from '../../icon/icon.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { RtTooltip } from '../../tooltip/tooltip.directive';

export type SegmentedButtonType<T> = {
  isActive: boolean;
  isAvailable: boolean;
  isDisabled?: boolean;
  value: T;
  label?: string | undefined;
  counter?: number | undefined | null;
  iconName?: IconName | undefined;
  iconColor?: IconToken | undefined;
  badgeVariant?: BadgeColor | undefined;
  badgeText?: string | undefined;
  isUnread?: boolean;
  suffixTpl?: TemplateRef<any>;
};

export type LanguageSwitcherItem = {
  code: string;
  name: string;
  primary?: boolean;
  isDefault: boolean;
  translationFieldsCount: number;
};

@Component({
  selector: 'rt-segmented-control-button',
  templateUrl: './segmented-control-button.component.html',
  styleUrls: ['./segmented-control-button.component.less'],
  host: {
    '[class.active-btn]': 'isActive()',
    '[class.disabled]': 'isDisabled()',
    '[class.full-width]': 'fullwidth()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BadgeComponent,
    CircularProgressIndicatorComponent,
    IconComponent,
    RtTooltip,
    SpinnerComponent,
    DotComponent,
    NgTemplateOutlet,
  ],
})
export class SegmentedControlButtonComponent {
  private readonly i18n = inject(I18nService);
  public readonly elRef = inject(ElementRef);

  public readonly type = input<'view-switcher' | 'multilang-switcher'>(undefined!);
  public readonly fullwidth = input<boolean>(undefined!);
  public readonly isActive = input<boolean>(undefined!);
  public readonly isAvailable = input<boolean>(undefined!);
  public readonly iconColor = input<IconToken>();
  public readonly isUnread = input<boolean>();
  public readonly isDisabled = input<boolean>();
  public readonly fieldsCount = input<number>(undefined!);
  public readonly loadingLanguageCode = input<string | null>(null);
  public readonly defaultTranslationKey = input<string>(undefined!);
  public readonly primaryTranslationKey = input<string>(undefined!);
  public readonly progressTooltipTranslationKey = input<string>(undefined!);
  public readonly showIndicatorOnDefault = input<boolean>(undefined!);

  @Input() public label: string | undefined;
  @Input() public counter: number | undefined | null;
  @Input() public iconName: IconName | undefined;
  @Input() public badgeVariant: BadgeColor | undefined;
  @Input() public badgeText: string | undefined;
  @Input() public language: LanguageSwitcherItem;
  @Input() public suffixTpl: TemplateRef<any> | null = null;

  public getTranslationProgress(translationFieldsCount: number): number {
    const fieldsCount = this.fieldsCount();
    return fieldsCount ? (translationFieldsCount / fieldsCount) * 100 : 0;
  }

  public getTooltipMessage(langTermsCount: number): string {
    return `${langTermsCount} / ${this.fieldsCount()} ${this.i18n.translate(
      this.progressTooltipTranslationKey() ?? 'app.languages.fields_translated',
    )}`;
  }

  public get bracketLabel(): string {
    const defaultTranslation = this.i18n.translate(
      this.defaultTranslationKey() ?? 'app.common.default',
    );
    const primaryTranslation = this.i18n.translate(
      this.primaryTranslationKey() ?? 'app.languages.primary',
    );
    if (this.language.isDefault && this.language.primary) {
      return `${defaultTranslation}, ${primaryTranslation}`;
    }
    if (this.language.isDefault) {
      return defaultTranslation;
    }
    if (this.language.primary) {
      return primaryTranslation;
    }
    return '';
  }
}
